import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/site/Layout";
import { EventEmptyState } from "@/components/site/EventEmptyState";
import { Calendar, X, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSelectedEvent } from "@/lib/event-context";
import { eventLabel, formatEventDateRange } from "@/lib/events";
import { useState, useEffect, useCallback } from "react";

// ── Types ────────────────────────────────────────────────────────────────────

interface GalleryItem {
  id: number;
  event_id: number;
  image_url: string;
  caption: string | null;
  subject: string | null;
  is_featured: boolean;
  sort_order: number;
  created_at: string;
}

interface GalleryGroup {
  subject: string;
  items: GalleryItem[];
}

interface GalleryData {
  event: { id: number; title: string; event_date: string | null; event_date_from?: string | null; event_date_to?: string | null };
  gallery_items: GalleryItem[];
  gallery_groups: GalleryGroup[];
}

// ── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/gallery")({
  component: Gallery,
  head: () => ({
    meta: [
      { title: "Gallery — UASA Events" },
      { name: "description", content: "Photo gallery from the selected UASA event." },
    ],
  }),
});

// ── Lightbox ─────────────────────────────────────────────────────────────────

interface LightboxProps {
  items: GalleryItem[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

function Lightbox({ items, index, onClose, onPrev, onNext }: LightboxProps) {
  const item = items[index];

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, onPrev, onNext]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/92 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image lightbox"
    >
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        aria-label="Close lightbox"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Prev */}
      {items.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          aria-label="Previous image"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}

      {/* Image */}
      <div className="flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
        <img
          src={item.image_url}
          alt={item.caption || item.subject || "Gallery image"}
          className="max-h-[80vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
        />
        {(item.caption || item.subject) && (
          <div className="text-center">
            {item.caption && (
              <p className="text-sm text-white/90">{item.caption}</p>
            )}
            {item.subject && (
              <p className="mt-1 text-xs font-medium uppercase tracking-widest text-white/50">
                {item.subject}
              </p>
            )}
          </div>
        )}
        <p className="text-xs text-white/40">
          {index + 1} / {items.length}
        </p>
      </div>

      {/* Next */}
      {items.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          aria-label="Next image"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}

// ── Image grid ───────────────────────────────────────────────────────────────

interface ImageGridProps {
  items: GalleryItem[];
  onOpen: (item: GalleryItem) => void;
}

function ImageGrid({ items, onOpen }: ImageGridProps) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 xl:grid-cols-5">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onOpen(item)}
          className="group relative aspect-square overflow-hidden rounded-xl bg-secondary shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label={item.caption || item.subject || "View image"}
        >
          <img
            src={item.image_url}
            alt={item.caption || item.subject || "Gallery image"}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
            loading="lazy"
          />
          {item.caption && (
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-left text-xs text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
              {item.caption}
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

const ALL_TAB = "All";

function Gallery() {
  const { selectedEvent, selectedEventId, loading: eventsLoading } = useSelectedEvent();
  const [groups, setGroups] = useState<GalleryGroup[]>([]);
  const [allItems, setAllItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter tab state
  const [activeTab, setActiveTab] = useState<string>(ALL_TAB);

  // Lightbox state — tracks the flat allItems index
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!selectedEventId) {
      setGroups([]);
      setAllItems([]);
      setLoading(false);
      return;
    }

    const loadGallery = async () => {
      setLoading(true);
      setActiveTab(ALL_TAB);
      try {
        const response = await api.get<GalleryData>(`/api/gallery/event/${selectedEventId}`);
        if (response.success && response.data) {
          setGroups(response.data.gallery_groups || []);
          setAllItems(response.data.gallery_items || []);
        } else {
          setGroups([]);
          setAllItems([]);
        }
      } catch (error) {
        console.error("Failed to load gallery:", error);
        setGroups([]);
        setAllItems([]);
      } finally {
        setLoading(false);
      }
    };

    loadGallery();
  }, [selectedEventId]);

  // Derive the items to show in the lightbox based on the active tab
  const lightboxItems: GalleryItem[] =
    activeTab === ALL_TAB
      ? allItems
      : groups.find((g) => g.subject === activeTab)?.items ?? [];

  const openLightbox = useCallback(
    (item: GalleryItem) => {
      const idx = lightboxItems.findIndex((i) => i.id === item.id);
      setLightboxIndex(idx >= 0 ? idx : 0);
    },
    [lightboxItems],
  );

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const prevImage = useCallback(() => {
    setLightboxIndex((i) =>
      i === null ? null : (i - 1 + lightboxItems.length) % lightboxItems.length,
    );
  }, [lightboxItems.length]);

  const nextImage = useCallback(() => {
    setLightboxIndex((i) =>
      i === null ? null : (i + 1) % lightboxItems.length,
    );
  }, [lightboxItems.length]);

  // Subject tabs: "All" + one per group
  const tabs = groups.length > 1 ? [ALL_TAB, ...groups.map((g) => g.subject)] : [];

  // Which groups to render
  const visibleGroups =
    activeTab === ALL_TAB ? groups : groups.filter((g) => g.subject === activeTab);

  return (
    <>
      <PageHero
        eyebrow="Photo Gallery"
        title="Event Highlights"
        subtitle={selectedEvent ? eventLabel(selectedEvent) : "Photos from the selected event"}
      />

      {eventsLoading || loading ? (
        <section className="px-4 py-16 text-center text-muted-foreground">
          Loading gallery…
        </section>
      ) : !selectedEvent ? (
        <EventEmptyState
          title="Choose an event"
          message="Select an event in the header to view its gallery."
        />
      ) : allItems.length === 0 ? (
        <EventEmptyState
          title="No photos yet"
          message="Gallery images have not been added for this event."
        />
      ) : (
        <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">

            {/* Event header */}
            <div className="mb-8 text-center sm:mb-10">
              <h2 className="font-serif text-2xl text-primary sm:text-3xl">
                {selectedEvent.title}
              </h2>
              {(selectedEvent.event_date_from || selectedEvent.event_date) && (
                <p className="mt-2 flex items-center justify-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  {formatEventDateRange(selectedEvent)}
                </p>
              )}
            </div>

            {/* Subject filter tabs — only shown when there are multiple subjects */}
            {tabs.length > 0 && (
              <div className="mb-8 flex flex-wrap justify-center gap-2 sm:gap-3">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={[
                      "rounded-full px-4 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                      activeTab === tab
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "bg-secondary text-secondary-foreground hover:bg-secondary/70",
                    ].join(" ")}
                  >
                    {tab}
                    {tab !== ALL_TAB && (
                      <span className="ml-1.5 text-xs opacity-60">
                        ({groups.find((g) => g.subject === tab)?.items.length ?? 0})
                      </span>
                    )}
                    {tab === ALL_TAB && (
                      <span className="ml-1.5 text-xs opacity-60">({allItems.length})</span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Gallery — one section per visible group */}
            <div className="space-y-12 sm:space-y-16">
              {visibleGroups.map((group) => (
                <div key={group.subject}>
                  {/* Section heading — always shown so users know which subject they're viewing */}
                  <div className="mb-5 flex items-center gap-4">
                    <h3 className="font-serif text-xl text-primary sm:text-2xl">
                      {group.subject}
                    </h3>
                    <span className="text-sm text-muted-foreground">
                      {group.items.length} {group.items.length === 1 ? "photo" : "photos"}
                    </span>
                    <div className="h-px flex-1 bg-border" />
                  </div>

                  <ImageGrid items={group.items} onOpen={openLightbox} />
                </div>
              ))}
            </div>

          </div>
        </section>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && lightboxItems.length > 0 && (
        <Lightbox
          items={lightboxItems}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}
    </>
  );
}
